import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Form, Input, Select, Radio, Button, TimePicker, Space, Modal, Alert } from 'antd';
import { SaveOutlined, CloseOutlined, ClockCircleOutlined, ArrowRightOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { message } from 'antd';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { attendanceApi, departmentsApi, companyAddressesApi, show422Error, ValidationError } from '../../../utils/api';
import moment from 'moment-jalaali';
import dayjs from 'dayjs';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';

const { Option } = Select;
const { TextArea } = Input;

const RegisterAttendance = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/attendance', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const [registerBy, setRegisterBy] = useState('month'); // 'month' or 'date'
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [leaveWarningModal, setLeaveWarningModal] = useState(false);
  const [leaveWarningData, setLeaveWarningData] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  useEffect(() => {
    const loadOptions = async () => {
      setDepartmentsLoading(true);
      setLocationsLoading(true);
      try {
        const [deptRes, locRes] = await Promise.all([
          departmentsApi.getAll(),
          companyAddressesApi.getAll(),
        ]);
        setDepartments(deptRes.data || []);
        setLocations(locRes || []);
      } catch (error) {
        console.error('خطا در دریافت داده‌ها:', error);
      } finally {
        setDepartmentsLoading(false);
        setLocationsLoading(false);
      }
    };
    loadOptions();
  }, []);

  const handleDepartmentChange = async (deptId) => {
    setSelectedDept(deptId);
    form.setFieldsValue({ employees: [] });
    if (deptId) {
      setEmployeesLoading(true);
      try {
        const res = await attendanceApi.getEmployeesByDepartment(deptId);
        setEmployees(res.data?.employees || res?.employees || []);
      } catch (error) {
        console.error('خطا در دریافت کارمندان:', error);
        message.error('خطا در دریافت کارمندان دپارتمان');
      } finally {
        setEmployeesLoading(false);
      }
    } else {
      setEmployees([]);
    }
  };

  // تابع تبدیل اعداد فارسی به انگلیسی
  const persianToEnglishDigits = (str) => {
    if (!str) return str;
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    return str.replace(/[۰-۹]/g, (digit) => englishDigits[persianDigits.indexOf(digit)]);
  };

  // تابع برای ساخت payload
  const buildPayload = (values) => {
    const markBy = values.registerBy || registerBy;
    
    // تبدیل زمان‌ها به فرمت HH:mm
    let clockInTime = null;
    let clockOutTime = null;
    
    if (values.entryTime) {
      clockInTime = dayjs.isDayjs(values.entryTime) 
        ? values.entryTime.format('HH:mm')
        : dayjs(values.entryTime).format('HH:mm');
    }
    
    if (values.exitTime) {
      clockOutTime = dayjs.isDayjs(values.exitTime)
        ? values.exitTime.format('HH:mm')
        : dayjs(values.exitTime).format('HH:mm');
    }

    const payload = {
      type: 'bulkMark',
      mark_attendance_by: markBy,
      user_id: values.employees || [],
      half_day: values.halfDay || 'no',
      half_day_duration: values.halfDayType || 'first_half',
      clock_in_time: clockInTime || null,
      clock_out_time: clockOutTime || null,
      late: values.delay || 'no',
      overwrite_attendance: 'no',
      working_from: null,
      work_from_type: values.entryWorkFrom || 'office',
      location: values.entryLocation ? Number(values.entryLocation) : null,
      clock_out_time_working_from: null,
      clock_out_time_work_from_type: values.exitWorkFrom || 'office',
      clock_out_time_location_id: values.exitLocation ? Number(values.exitLocation) : null,
    };

    if (markBy === 'month') {
      payload.year = Number(values.year);
      payload.month = String(values.month).padStart(2, '0');
      payload.multi_date = null;
    } else {
      payload.year = null;
      payload.month = null;
      // تبدیل بازه تاریخ به فرمت: "1404/08/01 - 1404/08/05"
      if (values.dateRange && Array.isArray(values.dateRange) && values.dateRange.length === 2) {
        // dateRange از PersianDatePicker آرایه‌ای از DateObject ها است
        const start = values.dateRange[0].format ? values.dateRange[0].format('YYYY/MM/DD') : values.dateRange[0];
        const end = values.dateRange[1].format ? values.dateRange[1].format('YYYY/MM/DD') : values.dateRange[1];
        // تبدیل اعداد فارسی به انگلیسی
        payload.multi_date = persianToEnglishDigits(`${start} - ${end}`);
      } else {
        payload.multi_date = null;
      }
    }

    return payload;
  };

  // تابع برای ساخت payload برای check-half-day
  const buildCheckHalfDayPayload = (values) => {
    const markBy = values.registerBy || registerBy;
    const payload = {
      type: 'bulkMark',
      mark_attendance_by: markBy,
      user_id: values.employees || [],
      half_day: values.halfDay || 'no',
      half_day_duration: values.halfDayType || 'first_half',
    };

    if (markBy === 'month') {
      payload.year = Number(values.year);
      payload.month = String(values.month).padStart(2, '0');
      payload.multi_date = null;
    } else {
      payload.year = null;
      payload.month = null;
      // تبدیل بازه تاریخ به فرمت: "1404/08/01 - 1404/08/05"
      if (values.dateRange && Array.isArray(values.dateRange) && values.dateRange.length === 2) {
        // dateRange از PersianDatePicker آرایه‌ای از DateObject ها است
        const start = values.dateRange[0].format ? values.dateRange[0].format('YYYY/MM/DD') : values.dateRange[0];
        const end = values.dateRange[1].format ? values.dateRange[1].format('YYYY/MM/DD') : values.dateRange[1];
        // تبدیل اعداد فارسی به انگلیسی
        payload.multi_date = persianToEnglishDigits(`${start} - ${end}`);
      } else {
        payload.multi_date = null;
      }
    }

    return payload;
  };

  // تابع برای ثبت نهایی حضور و غیاب
  const performBulkMark = async (payload) => {
    setLoading(true);
    try {
      console.log('Payload being sent:', JSON.stringify(payload, null, 2));
      await attendanceApi.bulkMark(payload);
      message.success('حضور و غیاب با موفقیت ثبت شد');
      goBackToList();
    } catch (error) {
      console.error('Error details:', error);
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ثبت حضور و غیاب');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // ساخت payload برای check-half-day
      const checkPayload = buildCheckHalfDayPayload(values);
      
      // بررسی اینکه آیا کارمند و تاریخ انتخاب شده است
      if (!checkPayload.user_id || !Array.isArray(checkPayload.user_id) || checkPayload.user_id.length === 0) {
        message.error('لطفا کارمند را انتخاب کنید');
        return;
      }

      // درخواست به check-half-day
      setLoading(true);
      const checkResponse = await attendanceApi.checkHalfDay(checkPayload);
      setLoading(false);

      const checkData = checkResponse.data || {};
      const hasLeave = checkData.halfDayExist || checkData.fullDayExist;

      if (hasLeave) {
        // نمایش Modal هشدار
        setLeaveWarningData(checkData);
        setPendingSubmitData(values);
        setLeaveWarningModal(true);
      } else {
        // اگر مرخصی نبود، مستقیماً ثبت کن
        const payload = buildPayload(values);
        await performBulkMark(payload);
      }
    } catch (error) {
      console.error('Error in check-half-day:', error);
      setLoading(false);
      
      // اگر خطای validation است، به اولین فیلد خطادار اسکرول کن
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
        return;
      }
      
      // در صورت خطا در check-half-day، باز هم ثبت را انجام می‌دهیم
      const payload = buildPayload(values);
      await performBulkMark(payload);
    }
  };

  // تابع برای تایید ثبت در صورت وجود مرخصی
  const handleConfirmSubmitWithLeave = async () => {
    setLeaveWarningModal(false);
    if (pendingSubmitData) {
      const payload = buildPayload(pendingSubmitData);
      await performBulkMark(payload);
    }
    setLeaveWarningData(null);
    setPendingSubmitData(null);
  };

  // تابع برای لغو ثبت در صورت وجود مرخصی
  const handleCancelSubmitWithLeave = () => {
    setLeaveWarningModal(false);
    setLeaveWarningData(null);
    setPendingSubmitData(null);
  };

  const handleCancel = () => {
    goBackToList();
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <ClockCircleOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ثبت حضور و غیاب</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ثبت گروهی حضور و غیاب کارمندان</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            initialValues={{
              registerBy: 'month',
              year: '1404',
              month: '9',
              entryWorkFrom: 'office',
              entryTime: dayjs('08:00', 'HH:mm'),
              exitWorkFrom: 'office',
              exitTime: dayjs('16:00', 'HH:mm'),
              delay: 'no',
              halfDay: 'no',
              halfDayType: 'first_half',
            }}
            style={{ fontFamily: 'CustomFont' }}
          >
            {/* Row 1: Department and Employees */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>دپارتمان</span>}
                name="department"
              >
                <Select
                  placeholder="انتخاب دپارتمان"
                  style={{ fontFamily: 'CustomFont' }}
                  onChange={handleDepartmentChange}
                  allowClear
                  loading={departmentsLoading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {departments.map((d) => (
                    <Option key={d.id} value={String(d.id)}>
                      {d.team_name || d.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    کارمندان
                  </span>
                }
                name="employees"
                rules={[{ required: true, message: 'لطفا کارمند را انتخاب کنید' }]}
              >
                <Select
                  placeholder="ابتدا دپارتمان را انتخاب کنید"
                  mode="multiple"
                  style={{ fontFamily: 'CustomFont' }}
                  disabled={!selectedDept}
                  loading={employeesLoading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {employees.map((e) => (
                    <Option key={e.id} value={Number(e.id)}>
                      {e.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 2: Register By */}
            <div className="mb-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>ثبت حضور توسط</span>}
                name="registerBy"
              >
                <Radio.Group 
                  value={registerBy} 
                  onChange={(e) => setRegisterBy(e.target.value)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  <Radio value="month">ماه</Radio>
                  <Radio value="date">بازه تاریخ</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            {registerBy === 'month' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>سال</span>}
                  name="year"
                  rules={[{ required: true, message: 'لطفا سال را انتخاب کنید' }]}
                >
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="1404">۱۴۰۴</Option>
                    <Option value="1403">۱۴۰۳</Option>
                    <Option value="1402">۱۴۰۲</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>ماه</span>}
                  name="month"
                  rules={[{ required: true, message: 'لطفا ماه را انتخاب کنید' }]}
                >
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="1">فروردین</Option>
                    <Option value="2">اردیبهشت</Option>
                    <Option value="3">خرداد</Option>
                    <Option value="4">تیر</Option>
                    <Option value="5">مرداد</Option>
                    <Option value="6">شهریور</Option>
                    <Option value="7">مهر</Option>
                    <Option value="8">آبان</Option>
                    <Option value="9">آذر</Option>
                    <Option value="10">دی</Option>
                    <Option value="11">بهمن</Option>
                    <Option value="12">اسفند</Option>
                  </Select>
                </Form.Item>
              </div>
            ) : (
              <div className="mb-4">
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>بازه تاریخ</span>}
                  name="dateRange"
                  rules={[{ required: true, message: 'لطفا بازه تاریخ را انتخاب کنید' }]}
                >
                  <PersianDatePicker 
                    range
                    style={{ width: '100%' }}
                    placeholder="انتخاب بازه تاریخ"
                  />
                </Form.Item>
              </div>
            )}

            {/* Row 3: Entry Details */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200" style={{ fontFamily: 'CustomFont' }}>
                ورود
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>موقعیت مکانی</span>}
                  name="entryLocation"
                >
                  <Select
                    placeholder="انتخاب موقعیت"
                    style={{ fontFamily: 'CustomFont' }}
                    allowClear
                    loading={locationsLoading}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                  >
                    {locations.map((loc) => (
                      <Option key={loc.id} value={String(loc.id)}>
                        {loc.location || loc.address || loc.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>کار از</span>}
                  name="entryWorkFrom"
                >
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="office">دفتر</Option>
                    <Option value="home">خانه</Option>
                    <Option value="other">سایر</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>زمان ورود</span>}
                  name="entryTime"
                  rules={[{ required: true, message: 'لطفا زمان ورود را وارد کنید' }]}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: '100%' }}
                    placeholder="08:00"
                  />
                </Form.Item>
              </div>
            </div>

            {/* Row 4: Exit Details */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200" style={{ fontFamily: 'CustomFont' }}>
                خروج
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>موقعیت مکانی</span>}
                  name="exitLocation"
                >
                  <Select
                    placeholder="انتخاب موقعیت"
                    style={{ fontFamily: 'CustomFont' }}
                    allowClear
                    loading={locationsLoading}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                  >
                    {locations.map((loc) => (
                      <Option key={loc.id} value={String(loc.id)}>
                        {loc.location || loc.address || loc.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>کار از</span>}
                  name="exitWorkFrom"
                >
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="office">دفتر</Option>
                    <Option value="home">خانه</Option>
                    <Option value="other">سایر</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>زمان خروج</span>}
                  name="exitTime"
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: '100%' }}
                    placeholder="16:00"
                  />
                </Form.Item>
              </div>
            </div>

            {/* Row 5: Delay and Half Day */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>تاخیر</span>}
                name="delay"
              >
                <Radio.Group style={{ fontFamily: 'CustomFont' }}>
                  <Radio value="yes">بله</Radio>
                  <Radio value="no">خیر</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>نیم روز</span>}
                name="halfDay"
              >
                <Radio.Group 
                  style={{ fontFamily: 'CustomFont' }}
                  onChange={(e) => {
                    if (e.target.value === 'no') {
                      form.setFieldsValue({ halfDayType: 'first_half' });
                    }
                  }}
                >
                  <Radio value="yes">بله</Radio>
                  <Radio value="no">خیر</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>نوع نیم روز</span>}
                name="halfDayType"
                hidden={form.getFieldValue('halfDay') !== 'yes'}
              >
                <Select style={{ fontFamily: 'CustomFont' }} placeholder="انتخاب نوع">
                  <Option value="first_half">نیمه اول</Option>
                  <Option value="second_half">نیمه دوم</Option>
                </Select>
              </Form.Item>
            </div>

            <div className="mb-6">
              <Alert
                type="info"
                showIcon
                className="custom-font"
                message={
                  <span>
                    برای اصلاح ساعت تردد قبلی از{' '}
                    <Link to="/hr/attendance">صفحه حضور و غیاب</Link>
                    {' '}یا{' '}
                    <Link to="/hr/attendance-regularizations">درخواست اصلاح تردد</Link>
                    {' '}استفاده کنید.
                  </span>
                }
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                size="middle"
                style={{ fontFamily: 'CustomFont' }}
              >
                لغو
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={loading}
                size="middle"
                className="bg-green-600 hover:bg-green-700 border-green-600"
                style={{ fontFamily: 'CustomFont' }}
              >
                ذخیره
              </Button>
            </div>
          </Form>
            </Card>
          </div>
        </Card>

        <Modal
          title={
            <span style={{ fontFamily: 'CustomFont' }}>
              <ExclamationCircleOutlined className="text-amber-500 mr-2" />
              هشدار: کارمند در مرخصی است
            </span>
          }
          open={leaveWarningModal}
          onOk={handleConfirmSubmitWithLeave}
          onCancel={handleCancelSubmitWithLeave}
          okText="بله، ادامه می‌دهم"
          cancelText="لغو"
          okButtonProps={{ danger: false, className: 'bg-green-600 hover:bg-green-700' }}
          style={{ fontFamily: 'CustomFont' }}
        >
          <div style={{ fontFamily: 'CustomFont' }}>
            <p>
              کارمند انتخاب شده در تاریخ مورد نظر در مرخصی است.
            </p>
            {leaveWarningData && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="mb-2"><strong>جزئیات:</strong></p>
                {leaveWarningData.halfDayExist && (
                  <p className="mb-1">• مرخصی نیم روز: بله</p>
                )}
                {leaveWarningData.fullDayExist && (
                  <p className="mb-1">• مرخصی تمام روز: بله</p>
                )}
                {leaveWarningData.attendanceDuration && (
                  <p className="mb-1">• مدت حضور: {leaveWarningData.attendanceDuration === 'first_half' ? 'نیمه اول' : 'نیمه دوم'}</p>
                )}
              </div>
            )}
            <p className="mt-4">
              آیا می‌خواهید با وجود مرخصی، حضور و غیاب را ثبت کنید؟
            </p>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default RegisterAttendance;
